import { useMemo, useState } from "react";

export type PaginationState = {
    currentPage: number,
    itemsPerPage: number,
    startIndex: number,
    endIndex: number,
}

export const defaultPagination: PaginationState = {
    currentPage: 1,
    itemsPerPage: 10,
    startIndex: 1,
    endIndex: 10,
}

export type PaginationControl = {
    SetNextPage: () => void,
    SetPreviousPage: () => void,
    SetPerPage: (value: number) => void,
    SetPage: (value: number) => void
} & PaginationState

export function usePagination<Type>(listToBePaginated: Type[], componentKey: string): PaginationControl {
    const [paginationState, setPaginationState] = useState<PaginationState>(defaultPagination);
    const pages = useMemo(() => {
        return Math.ceil(listToBePaginated.length / paginationState.itemsPerPage)
    }, [listToBePaginated, paginationState.itemsPerPage])

    const SetPage = (page: number) => {
        if (page <= 0)
            page = 1
        if (page > pages)
            page = pages

        setPaginationState((current: PaginationState) => {
            return {
                ...current,
                currentPage: page
            }
        })
    }

    const SetNextPage = () => SetPage(paginationState.currentPage + 1);
    const SetPreviousPage = () => SetPage(paginationState.currentPage - 1);

    const SetPerPage = (perPage: number) => {
        setPaginationState((current: PaginationState) => {
            return {
                ...current,
                currentPage: 1,
                itemsPerPage: perPage
            }
        })

        if (paginationState.currentPage > pages)
            SetPage(pages)
    }

    const startIndex = (paginationState.currentPage - 1) * paginationState.itemsPerPage;
    const endIndex = (
        startIndex + paginationState.itemsPerPage) > listToBePaginated.length ?
        listToBePaginated.length
        : startIndex + paginationState.itemsPerPage;

    return {
        ...paginationState,
        startIndex,
        endIndex,
        SetNextPage,
        SetPreviousPage,
        SetPerPage,
        SetPage
    };
}